/* eslint-disable import/prefer-default-export */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { checkStockImpact } from '../services/movements/movementsApi';

// ---------------------------------------------------------------------------
// Warn guard de saldo negativo (handoff 0093 §5a) — simétrico ao
// useMovementDuplicateCheck (rework 2026-08-16):
//
// - `triggerCheck` roda quando a lista de itens/PDV muda (a página dispara
//   via useEffect — ações explícitas) e no "↻ Atualizar" do hint. Alimenta SÓ
//   o indicador — o detalhe abre pelo "ver" (`reopenModal`), nunca sozinho.
// - `checkNow` é a guarda do Salvar: consulta fresca que RETORNA
//   `negative_stock[]` (sem abrir modal) — o modal unificado de confirmação
//   da página exibe o achado.
// - SALDO NÃO SE CACHEIA: dado vivo, alterado por outras telas/operadores a
//   qualquer momento. Toda consulta é fresca; só há dedupe de requisições EM
//   VOO da mesma chave canônica (movement_id + pdv + move_type + itens
//   ordenados por prod_id).
// - Falha do check (rede/4xx/403) NUNCA bloqueia: vira status `skipped`,
//   silencioso pro usuário (console.warn pra diagnóstico) — o recurso é
//   informativo (estoque negativo é aceito).
//
// `status`: idle | checking | clear | found | skipped. `movementId` só na
// edição (o backend devolve o efeito atual do movimento antes de calcular).
// ---------------------------------------------------------------------------

function buildKey({ movementId, pdvId, moveType, items }) {
   if (!pdvId || !moveType || !items?.length) return '';
   const itemsKey = [...items]
      .sort((a, b) => a.prod_id - b.prod_id)
      .map((item) => `${item.prod_id}x${item.prod_qtd}`)
      .join(',');
   return `${movementId ?? ''}|${pdvId}|${moveType}|${itemsKey}`;
}

export function useStockImpactCheck({ movementId, pdvId, moveType, items }) {
   // Payload mínimo pro backend — as listas das telas carregam extras de UI
   // (sku, prodName...) que não pertencem ao contrato do check.
   const checkItems = useMemo(
      () =>
         (items ?? []).map((item) => ({
            prod_id: Number(item.prod_id),
            prod_qtd: Number(item.prod_qtd),
         })),
      [items]
   );

   const key = buildKey({ movementId, pdvId, moveType, items: checkItems });

   const [status, setStatus] = useState('idle');
   const [negativeStock, setNegativeStock] = useState([]);
   const [isModalOpen, setIsModalOpen] = useState(false);

   const inflightRef = useRef(new Map()); // chave → Promise em voo
   const currentKeyRef = useRef(key);

   // Sincroniza o indicador quando a combinação muda (adicionar um item
   // invalida o "✓" na hora). Sem restauração de resultado antigo — o gatilho
   // da página re-checa em seguida e o indicador se atualiza fresco.
   useEffect(() => {
      currentKeyRef.current = key;
      if (!key) {
         setStatus('idle');
         setNegativeStock([]);
         return;
      }
      setStatus(inflightRef.current.has(key) ? 'checking' : 'idle');
      setNegativeStock([]);
   }, [key]);

   const applyResultToIndicator = useCallback((k, result) => {
      if (currentKeyRef.current !== k) return; // resposta de combinação antiga
      if (result === 'error') {
         setStatus('skipped');
         setNegativeStock([]);
      } else {
         setStatus(result.length ? 'found' : 'clear');
         setNegativeStock(result);
      }
   }, []);

   // Consulta SEMPRE fresca — sem cache de resultado. Só deduplica requisição
   // em voo da mesma chave (adicionar item + Salvar em seguida = 1 request,
   // e o Salvar espera essa resposta, que é atual por definição).
   const fetchFresh = useCallback(
      (k, params) => {
         if (inflightRef.current.has(k)) {
            return inflightRef.current.get(k);
         }
         if (currentKeyRef.current === k) setStatus('checking');

         const promise = checkStockImpact(params)
            .catch((err) => {
               // Nunca bloqueia nem toasta (0093 §5) — mas fica diagnosticável.
               // eslint-disable-next-line no-console
               console.warn('[stock-impact-check] consulta falhou:', err);
               return 'error';
            })
            .then((result) => {
               inflightRef.current.delete(k);
               applyResultToIndicator(k, result);
               return result;
            });

         inflightRef.current.set(k, promise);
         return promise;
      },
      [applyResultToIndicator]
   );

   // Gatilho "cedo" (mudança da lista/PDV, "↻ Atualizar") — SÓ o indicador.
   const triggerCheck = useCallback(() => {
      if (!key) return;
      fetchFresh(key, { movementId, pdvId, moveType, items: checkItems });
   }, [key, movementId, pdvId, moveType, checkItems, fetchFresh]);

   // Guarda do Salvar: consulta fresca que retorna `negative_stock[]` ([] em
   // falha — não-bloqueante) para o modal unificado de confirmação da página.
   const checkNow = useCallback(async () => {
      if (!key) return [];
      const result = await fetchFresh(key, {
         movementId,
         pdvId,
         moveType,
         items: checkItems,
      });
      return Array.isArray(result) ? result : [];
   }, [key, movementId, pdvId, moveType, checkItems, fetchFresh]);

   // Viewer de detalhe, aberto pelo indicador "⚠ … — ver".
   const reopenModal = useCallback(() => {
      if (!negativeStock.length) return;
      setIsModalOpen(true);
   }, [negativeStock]);

   const closeModal = useCallback(() => {
      setIsModalOpen(false);
   }, []);

   // Usado pelo "Limpar" da página.
   const reset = useCallback(() => {
      inflightRef.current.clear();
      setIsModalOpen(false);
      setStatus('idle');
      setNegativeStock([]);
   }, []);

   return {
      status,
      negativeStock,
      isModalOpen,
      triggerCheck,
      checkNow,
      reopenModal,
      closeModal,
      reset,
   };
}
